import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useGlobalContext } from "../GlobalContext";

const useTasks = () => {
  const { token, baseurl, accessToken, setIsUnauthorised, AdminId } =
    useGlobalContext();

  const [loading, setLoading] = useState(false);

  // ===== GET TASKS =====
  const fetchTasks = async (fromDate, toDate, setTasks) => {
    setLoading(true);
    try {
      const payload = {
        driverId: "",
        fromDate,
        toDate,
        adminId: AdminId,
        status: [
          "unassigned",
          "assigned",
          "started",
          "inprogress",
          "successful",
          "food is ready",
          "acknowledged",
          "failed",
          "declined",
          "cancelled",
          "picked up",
          "arrived",
        ],
      };

      const res = await axios.post(
        `${baseurl}/api/Task/v1.0/getTasks`,
        payload,
        { headers: { Authorization: `Basic ${token}` } },
      );

      if (res.data.responseCode === "00") {
        setTasks(res.data.data || []);
      } else if (res.data.responseCode === "401") {
        setIsUnauthorised(true);
      } else {
        toast.error(res.data.responseMessage || "Failed to fetch tasks");
        setTasks([]);
      }
    } catch {
      toast.error("Error fetching tasks");
    } finally {
      setLoading(false);
    }
  };

  // ===== CHANGE TASK STATUS =====
  const changeTaskStatus = async (task, newStatus, refresh) => {
    if (!task || !newStatus) return;

    setLoading(true);
    try {
      const payload = {
        taskId: task.pk_task_id.toString(),
        status: newStatus,
        driverId: task.driver_id?.toString() || "0",
        adminId: AdminId,
      };

      const res = await axios.post(
        `${baseurl}/api/Task/v1.0/changeTaskStatusByPackky`,
        payload,
        { headers: { Authorization: `Basic ${token}` } },
      );

      if (res.data.responseCode === "00") {
        toast.success(res.data.responseMessage || "Status updated.");
        await refresh();
      } else if (res.data.responseCode === "401") {
        setIsUnauthorised(true);
      } else {
        toast.error(res.data.responseMessage || "Failed to update status.");
      }
    } catch {
      toast.error("Error changing task status.");
    } finally {
      setLoading(false);
    }
  };

  // ===== DELETE TASK =====
  const deleteTask = async (taskId, refresh) => {
    try {
      const res = await axios.post(
        `${baseurl}/api/Task/v1.0/deleteTask`,
        { taskId, adminId: AdminId },
        { headers: { Authorization: `Bearer ${accessToken}` } },
      );

      if (res.data.responseCode === "00") {
        toast.success(res.data.responseMessage);
        await refresh();
      } else if (res.data.responseCode === "401") {
        setIsUnauthorised(true);
      } else {
        toast.error(res.data.responseMessage || "Failed to delete task");
      }
    } catch {
      toast.error("Error while deleting task");
    }
  };

  // ===== TASK LOGS =====
  const fetchTaskLogs = async (
    taskId,
    fromDate,
    toDate,
    setAssignmentLogs,
    setStatusChangeLogs,
  ) => {
    const payload = {
      fromDate,
      toDate,
      taskId: taskId.toString(),
      adminId: AdminId,
    };

    try {
      const assignRes = await axios.post(
        `${baseurl}/api/Task/v1.0/getTaskAssignmentLogs`,
        payload,
        { headers: { Authorization: `Bearer ${accessToken}` } },
      );

      setAssignmentLogs(
        assignRes.data.responseCode === "00" ? assignRes.data.data || [] : [],
      );

      const statusRes = await axios.post(
        `${baseurl}/api/Task/v1.0/getTaskStatusChangeLogs`,
        payload,
        { headers: { Authorization: `Bearer ${accessToken}` } },
      );

      setStatusChangeLogs(
        statusRes.data.responseCode === "00" ? statusRes.data.data || [] : [],
      );
    } catch {
      toast.error("Error fetching task logs.");
      setAssignmentLogs([]);
      setStatusChangeLogs([]);
    }
  };

  // ===== REQUEST LOCATION =====
  const requestLocation = async (orderId, refresh) => {
    try {
      const res = await axios.post(
        `${baseurl}/api/Task/v1.0/locationRequest`,
        { orderId, adminId: AdminId },
        { headers: { Authorization: `Basic ${token}` } },
      );

      if (res.data.responseCode === "00") {
        toast.success(res.data.responseMessage);
        await refresh();
      } else if (res.data.responseCode === "401") {
        setIsUnauthorised(true);
      } else {
        toast.error(res.data.responseMessage || "Failed to request location");
      }
    } catch {
      toast.error("Error requesting location");
    }
  };

  return {
    fetchTasks,
    changeTaskStatus,
    deleteTask,
    fetchTaskLogs,
    requestLocation,
    loading,
  };
};

export default useTasks;
